/**
 * Case search drawer logic (FR-04, sc-11033): selection, labels, the rule
 * preview and the viewer highlight for a clicked hit. Pure, so it is unit-tested
 * apart from the drawer component.
 */
import type {
  CaseSearchBox,
  CaseSearchDocument,
  CaseSearchHit,
  CaseSearchRedactResult,
  CaseSearchResult,
} from '../../types';

/** Same minimum the server enforces: letters and digits in the query. */
export const MIN_QUERY_CHARS = 2;
/** Most case rules "Also always redact in this case" adds (server caps the same). */
export const MAX_RULE_WORDS = 10;

export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/** Letters and digits in the query (what the server needs at least two of). */
export function queryCharCount(query: string): number {
  return (query.match(/[\p{L}\p{N}]/gu) ?? []).length;
}

export function canSearch(query: string): boolean {
  return queryCharCount(query) >= MIN_QUERY_CHARS && query.trim().length <= 200;
}

/** A hit the reviewer can still redact: not covered yet and positioned (PDF). */
export function isSelectable(hit: CaseSearchHit): boolean {
  return !hit.redacted && hit.redactable;
}

export function selectableHits(result: CaseSearchResult | null): CaseSearchHit[] {
  if (!result) return [];
  return result.documents.flatMap((d) => d.hits.filter(isSelectable));
}

/** Drop ids that are no longer selectable (redacted since, or gone after a new search). */
export function pruneSelection(selected: ReadonlySet<string>, result: CaseSearchResult | null): Set<string> {
  const keep = new Set(selectableHits(result).map((h) => h.id));
  return new Set([...selected].filter((id) => keep.has(id)));
}

export function toggleHit(selected: ReadonlySet<string>, hitId: string): Set<string> {
  const next = new Set(selected);
  if (next.has(hitId)) next.delete(hitId);
  else next.add(hitId);
  return next;
}

export interface DocumentToggle {
  label: string;
  /** Every selectable hit in the document is selected. */
  allSelected: boolean;
  disabled: boolean;
}

export function documentToggle(doc: CaseSearchDocument, selected: ReadonlySet<string>): DocumentToggle {
  const open = doc.hits.filter(isSelectable);
  if (open.length === 0) {
    const anyRedacted = doc.hits.some((h) => h.redacted);
    return { label: anyRedacted ? 'All redacted' : 'Nothing to select', allSelected: false, disabled: true };
  }
  const allSelected = open.every((h) => selected.has(h.id));
  return { label: allSelected ? 'Clear' : `Select ${open.length}`, allSelected, disabled: false };
}

/** Select every open hit of the document, or clear them when all are selected. */
export function toggleDocument(selected: ReadonlySet<string>, doc: CaseSearchDocument): Set<string> {
  const next = new Set(selected);
  const open = doc.hits.filter(isSelectable);
  const allSelected = open.length > 0 && open.every((h) => next.has(h.id));
  for (const hit of open) {
    if (allSelected) next.delete(hit.id);
    else next.add(hit.id);
  }
  return next;
}

export function countLabel(result: CaseSearchResult | null): string {
  if (!result || result.total_hits === 0) return 'No hits';
  return `${plural(result.total_hits, 'hit')} in ${plural(result.documents.length, 'document')}`;
}

export function documentStatusLabel(doc: CaseSearchDocument): string {
  if (doc.out_of_date) return 'Finalized, out of date';
  return doc.finalized ? 'Finalized' : 'Not finalized';
}

/** Distinct spellings the case rules would be added for: the query, then each
 *  other spelling found (case-insensitive dedupe), as the server does. */
export function ruleWords(query: string, result: CaseSearchResult | null): string[] {
  const words = new Map<string, string>();
  const add = (word: string) => {
    const w = word.split(/\s+/).filter(Boolean).join(' ');
    if (w && !words.has(w.toLowerCase())) words.set(w.toLowerCase(), w);
  };
  add(query);
  for (const doc of result?.documents ?? []) for (const hit of doc.hits) add(hit.text);
  return [...words.values()].slice(0, MAX_RULE_WORDS);
}

/** What "Also always redact in this case" will do, in words. Case rules match a
 *  spelling exactly (any case, whole word), not loosely — say so. */
export function rulePreview(query: string, result: CaseSearchResult | null): string {
  const words = ruleWords(query, result);
  if (words.length === 0) return '';
  const quoted = words.map((w) => `“${w}”`).join(', ');
  const noun = words.length === 1 ? 'a case rule' : `${words.length} case rules`;
  return (
    `Adds ${noun} for ${quoted}: exact spelling, any case, also in documents added later. ` +
    'Other spellings (other spacing or dashes) are not caught by the rule.'
  );
}

export function resultMessage(outcome: CaseSearchRedactResult, categoryLabel: string): string {
  if (outcome.redacted_hits === 0) {
    return outcome.skipped.length > 0
      ? `Nothing redacted: ${outcome.skipped[0].reason.toLowerCase()}.`
      : 'Nothing to redact.';
  }
  let msg =
    `Redacted ${plural(outcome.redacted_hits, 'hit')} in ${plural(outcome.documents.length, 'document')} ` +
    `as ${categoryLabel} (approved).`;
  if (outcome.rules_added.length > 0) {
    msg += ` Added ${outcome.rules_added.length === 1 ? 'a case rule' : `${outcome.rules_added.length} case rules`}.`;
  }
  const skipped = outcome.skipped.length;
  if (skipped > 0) msg += ` ${plural(skipped, 'hit')} skipped.`;
  return msg;
}

/** Where the viewer goes and what it highlights when a hit is clicked. */
export interface SearchFocus {
  /** Changes on every click, so clicking the same hit again re-centres it. */
  key: string;
  hitId: string;
  documentId: string;
  /** 0-indexed page, or null for a text-based (DOCX/email) document. */
  page: number | null;
  boxes: CaseSearchBox[];
  /** For text-based viewers: which occurrence of `text` to highlight. */
  text: string;
  occurrence: number;
}

let focusCounter = 0;

export function focusForHit(hit: CaseSearchHit): SearchFocus {
  focusCounter += 1;
  return {
    key: `${hit.id}#${focusCounter}`,
    hitId: hit.id,
    documentId: hit.document_id,
    page: hit.page,
    boxes: hit.boxes,
    text: hit.text,
    occurrence: hit.occurrence,
  };
}

/** The focus's boxes on one (0-indexed) page. */
export function boxesOnPage(focus: SearchFocus | null | undefined, page: number): CaseSearchBox[] {
  if (!focus) return [];
  return focus.boxes.filter((b) => b.page === page);
}

/** Which of `count` DOM matches is the hit: its occurrence when it exists, else
 *  the first (the rendered HTML can differ slightly from the extracted text). */
export function pickOccurrence(count: number, occurrence: number): number {
  if (count <= 0) return -1;
  return occurrence >= 0 && occurrence < count ? occurrence : 0;
}

/** Ids of finalized documents a redaction just made out of date. */
export function outOfDateIds(outcome: CaseSearchRedactResult | null): string[] {
  return outcome?.out_of_date_documents.map((d) => d.document_id) ?? [];
}
